<script setup lang="ts">

import type ChatParticipant from '@/types/ChatParticipant.ts'
import PlayerName from '@/components/Objects/PlayerName.vue'
import { usePlayerRelationStore } from '@/stores/playerRelations.ts'
import { useAllianceStore } from '@/stores/allianceStore.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { computed } from 'vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import { CDN } from '@/models/cdn.ts'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Props
 */
const props = defineProps<{
  participant: ChatParticipant
  roomType: string
}>()

/**
 * Stores
 */
const playerRelationStore = usePlayerRelationStore()
const allianceStore = useAllianceStore()
const gameStore = useGameStore()

/**
 * Computed
 */
const playerImage = computed(() => {
  return playerRelationStore.getProfileImageByPlayerId(props.participant.playerId)
})

const playerCountry = computed(() => {
  return playerRelationStore.getCountryByPlayerId(props.participant.playerId)
})

const isLeader = computed(() => {
  return playerRelationStore.isAllianceLeader(props.participant.playerId)
})

const isAllianceChat = computed(() => {
  return props.roomType === 'alliance'
})

const sciencePercentage = computed(() => {
  if (!isAllianceChat.value || !gameStore.player?.allianceId) return 0
  
  const alliance = allianceStore.getAlliance(gameStore.player.allianceId)
  if (!alliance || !alliance.membersResearchPercentages) return 0
  
  const memberData = alliance.membersResearchPercentages.find(
    (member) => member.playerId === props.participant.playerId
  )
  
  return memberData?.scienceAllocationPercentage || 0
})

</script>

<template>
  <div class="flex justify-between bg-slate-900 bg-opacity-30 rounded mb-0.5 py-1 px-1.5">
    <img
      :src="'https://wsrv.nl/?url=' + playerImage + '&w=56&h=56'"
      class="rounded w-10 h-10 object-cover mr-2"
      alt="Chat">
    <div class="flex-grow flex flex-col justify-start">
      <div class="flex items-center justify-between">
        <p class="truncate w-44"><PlayerName :player-id="participant.playerId" /></p> <GenericIcon v-if="isLeader && isAllianceChat" type="vip" :title="t('communication.chat.leader')" class="ml-1" />
      </div>
      <div class="flex items-center justify-between mt-1">
        <img :src="CDN.countryFlag(playerCountry, '32x24')" class="h-3 rounded" alt="Country flag">
        <span v-if="isAllianceChat" class="text-xs" :class="sciencePercentage > 0 ? 'text-blue-400' : 'text-red-400'" :title="t('communication.chat.scienceAllocation')">
          {{ sciencePercentage }}%
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">

</style>